import { useState } from "react";
import type { OrderDocumentAddressCandidate } from "~/shared/documents/order-document-source";
import { Select } from "~/ui";
import { type DocumentShippableEntity, formatDocumentShippableBlock } from "~/utils/address-formatters";

type Props = {
  label: string;
  candidates: readonly OrderDocumentAddressCandidate[];
  value: DocumentShippableEntity | null | undefined;
  onChange: (address: DocumentShippableEntity) => void;
  preferredCandidateId?: string;
  defaultAddress?: DocumentShippableEntity | null;
  defaultLabel?: string;
};

const MANUAL_ADDRESS = "manual";
const DEFAULT_ADDRESS = "document-default";

/** Copies a saved address into this document only; existing document-specific defaults stay authoritative. */
export const DocumentAddressSourceSelect = ({
  label,
  candidates,
  value,
  onChange,
  preferredCandidateId,
  defaultAddress,
  defaultLabel = "Document default",
}: Props) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const block = value ? formatDocumentShippableBlock(value) : "";
  const defaultBlock = defaultAddress ? formatDocumentShippableBlock(defaultAddress) : "";
  const hasDefaultOption = !!defaultBlock && !candidates.some(candidate => candidate.block === defaultBlock);
  // Preserve the chosen source when different sources contain the same address.
  const matchedCandidate =
    candidates.find(candidate => candidate.id === selectedId && candidate.block === block) ??
    candidates.find(candidate => candidate.id === preferredCandidateId && candidate.block === block) ??
    candidates.find(candidate => candidate.block === block);
  const selectedValue =
    selectedId === MANUAL_ADDRESS
      ? MANUAL_ADDRESS
      : (matchedCandidate?.id ?? (hasDefaultOption && block === defaultBlock ? DEFAULT_ADDRESS : MANUAL_ADDRESS));

  if (candidates.length === 0 && !hasDefaultOption) {
    return null;
  }

  return (
    <Select
      w={{ base: "100%", sm: "50%" }}
      label={label}
      description="Choose a saved address or edit the address below for this document."
      searchable={false}
      clearable={false}
      allowDeselect={false}
      data={[
        ...(hasDefaultOption ? [{ value: DEFAULT_ADDRESS, label: defaultLabel }] : []),
        ...candidates.map(candidate => ({ value: candidate.id, label: candidate.label })),
        { value: MANUAL_ADDRESS, label: "Manual address" },
      ]}
      value={selectedValue}
      onChange={id => {
        if (!id) {
          return;
        }
        setSelectedId(id);
        const address = id === DEFAULT_ADDRESS ? defaultAddress : candidates.find(candidate => candidate.id === id)?.address;
        if (address) {
          onChange(address);
        }
      }}
    />
  );
};
